// Third party
import React, { Component, CSSProperties } from 'react';
import Dotdotdot from 'react-dotdotdot';
import classNames from 'classnames';
import ReactDOM from 'react-dom';
import { StickyContainer, Sticky } from 'react-sticky';
import camelCase from 'lodash/camelCase';
import DOMPurify from 'dompurify';

// Components
import { HoldingIcon } from '../../../components/common';
import ComparisonCardMenu from './ComparisonCardMenu';
import ComparisonValueCell from './ComparisonValueCell';
import ComparisonBaseCell from './ComparisonBaseCell';
import ComparisonCardWarnings from './ComparisonCardWarnings';
import { Tooltip, DisclaimerTooltip } from '../../../components/tooltips';
import ComparisonBenchmark from '../../../components/edit-benchmark/ComparisonBenchmark';
import { CommentExpanded } from '../../../components/markup-tools';
import { PrimaryLink, IconButton } from '../../../components/buttons';
import { GenericTextModal } from '../../../components/modals';
import ComparisonCardHoldingIcon from './ComparisonCardHoldingIcon';
import ComparisonRiskProfileLabel from '../misc/ComparisonRiskProfileLabel';
import ShelfStatus from '../../../components/shelf-management/ShelfStatus';
import ComparisonItemGroups from './ComparisonItemGroups';
import ComparisonGroupsMenu from './ComparisonGroupsMenu';
import ComparisonCardContacts from './ComparisonCardContacts';

// Utils
import { FormattedMessage, formatMessage, formatLocalizedString } from '../../../../utils/i18n';
import { DISCLAIMER_KEYS, DISCLAIMER_TAG } from '../../../../utils/disclaimers/constants';
import { isBenchmark, comparisonCategoryIsShown, VALID_GROUP_PRODUCTS, shouldExpandPortfolioInReadOnly } from '../../../../utils/comparison';
import { timeSince } from '../../../../utils/time';
import { getUniqueFieldId } from '../../../../utils/markup-tools';
import { COMPARISON_CATEGORIES, COMPARISON_ITEM_TYPES } from '../../../../utils/constants';
import { CurrencyLabel, PortfolioTypeLabel } from '../../../components/misc';
import { userHasPermissionEnabled, PERMISSIONS } from '../../../../utils/permissions';
import { CURRENCY } from '../../../../utils/currency';
import { getShortHistoryDatesForItem, getCustomDisclaimersForItem } from '../../../../utils/disclaimers/comparison';
import CustomTypePills from '../../../components/flaggableInstruments/CustomTypePills';
import { SelectInput, SelectOption } from '../../../components/inputs';
import ComparisonItem from '@/types/comparison-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import Comparison from '@/types/comparison';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import Portfolio from '@/types/portfolio';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonField from '@/types/comparison-field';
import SelectedCategories from '@/types/selected-categories';
import RiskPeriods from '@/types/risk-periods';
import ComparisonHolding from '@/types/comparison-holding';
import ComparisonItemGroup from '@/types/comparison-item-group';
import BasicUserInfos from '@/types/basic-user-infos';
import Locale from '@/types/locale';

type Props = {
  item: ComparisonItem;
  isFXSubItem: boolean;
  isExpandSubItem: boolean;
  sharingInfo?: Nullable<{ isMine: boolean; label: string }>;
  readOnly?: boolean;
  user?: User | null;
  comparison: Comparison;
  disableSaving?: boolean;
  editedBenchmark?: BenchmarkPortfolio;
  editedPortfolio?: Portfolio;
  onItemRemove: (item: ComparisonItem) => void;
  onModifyPortfolio: (item: ComparisonItem) => void;
  onViewPortfolio: (item: ComparisonItem) => void;
  onClonePortfolio: (item: ComparisonItem) => void;
  addCategoryBenchmark: (item: ComparisonItem) => void;
  onCloneBenchmark: (item: ComparisonItem) => void;
  onSavePortfolio: (item: ComparisonItem, toContact?: boolean) => void;
  onModifyBenchmark: (item: ComparisonItem) => void;
  categories: ComparisonCategory[];
  markupMode?: boolean;
  markupTool?: string;
  onCellClick: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField) => void;
  viewMarkup: boolean;
  previewMarkup?: boolean;
  selectedCategories?: SelectedCategories;
  setMarkupComment: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, comment: string) => void;
  postMarkupComment: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, dotNumber: number, comment: string) => void;
  getNewDotNumber: () => number;
  clearMarkup: (item: ComparisonItem, categoryKey: string, fieldKey: string, markupTool: string) => void;
  updateTableHeight: (height: number) => void;
  resizeCommentLinesContainer: () => void;
  riskPeriod: RiskPeriods;
  onShowCorrelation: (comparisonItem: ComparisonItem) => void;
  onExpandPortfolioItem: (item: ComparisonItem, expand: boolean) => void;
  onToggleFXItem?: (item: ComparisonItem, toggled: boolean) => void;
  showMenu: boolean;
  menuIsDisabled?: boolean;
  allowExpandPortfolio?: boolean;
  getAdditionalCategoryStyles: (category: ComparisonCategory) => CSSProperties;
  isEdited: boolean;
  disableStickyHeader?: boolean;
  updateComparisonItemHolding: (item: ComparisonItem, holdingId: string, holdingData: Partial<ComparisonHolding>) => void;
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
  popoverContainerRef: Nullable<HTMLDivElement>;
  scrollbarContainerRef?: HTMLElement;
  isSelected: boolean;
  hasStickyHeader?: boolean;
  onAddItemToGroup?: (item: ComparisonItem, group: ComparisonItemGroup, value: number, fee?: number) => void;
  onRemoveItemFromGroup?: (item: ComparisonItem, group: ComparisonItemGroup) => void;
  onModifyGroup: (item: ComparisonItem) => void;
  showGroups?: boolean;
  editGroups?: boolean;
  locale?: Locale;
};

type State = {
  showCustomDisclaimerModal: boolean;
};

class LegacyComparisonCardContent extends Component<Props, State> {
  // These should be in order of priority -- first disclaimer that exists on the item will be displayed.
  HEADER_DISCLAIMERS = {
    [COMPARISON_ITEM_TYPES.FUND]: [
      DISCLAIMER_KEYS.FUND_NO_DATA,
      DISCLAIMER_KEYS.FUND_CLOSED,
      DISCLAIMER_KEYS.FUND_LESS_THAN_3Y,
      DISCLAIMER_KEYS.FUND_LESS_THAN_1Y,
      DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK,
      DISCLAIMER_KEYS.FUND_HAS_SAME_BENCHMARK,
    ],
    [COMPARISON_ITEM_TYPES.STOCK]: [
      DISCLAIMER_KEYS.STOCK_NO_DATA,
      DISCLAIMER_KEYS.STOCK_CLOSED,
      DISCLAIMER_KEYS.STOCK_LESS_THAN_3Y,
      DISCLAIMER_KEYS.STOCK_LESS_THAN_1Y,
    ],
    [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
      DISCLAIMER_KEYS.PORTFOLIO_CLOSED_HOLDINGS,
      DISCLAIMER_KEYS.PORTFOLIO_LESS_THAN_1Y,
      DISCLAIMER_KEYS.PORTFOLIO_LESS_THAN_3Y,
      DISCLAIMER_KEYS.PORTFOLIO_MISSING_BENCHMARK,
    ],
    [COMPARISON_ITEM_TYPES.BENCHMARK]: [
      DISCLAIMER_KEYS.BENCHMARK_NO_DATA,
      DISCLAIMER_KEYS.BENCHMARK_CLOSED,
      DISCLAIMER_KEYS.BENCHMARK_LESS_THAN_3Y,
      DISCLAIMER_KEYS.BENCHMARK_LESS_THAN_1Y,
    ],
    [COMPARISON_ITEM_TYPES.GROUP]: [
      DISCLAIMER_KEYS.GROUP_CLOSED_HOLDINGS,
      DISCLAIMER_KEYS.GROUP_LESS_THAN_1Y,
      DISCLAIMER_KEYS.GROUP_LESS_THAN_3Y,
      DISCLAIMER_KEYS.GROUP_MISSING_BENCHMARK,
    ],
    [COMPARISON_ITEM_TYPES.ALT]: [],
  };

  static defaultProps = {
    showMenu: true,
    isFXSubItem: false,
    isExpandSubItem: false,
    isEdited: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      showCustomDisclaimerModal: false,
    };
  }

  componentDidMount() {
    const { item } = this.props;
    const commentSection = document.getElementById(`comment-section-${item._id}`);

    // Fixes issue when combining two items with comments and then cancelling
    if (item.markup && commentSection) {
      this.forceUpdate();
    }
  }

  getCardHeaderDisclaimer = () => {
    const { item } = this.props;
    const { disclaimers } = item;

    if (!disclaimers) {
      return null;
    }

    const disclaimerKey = this.HEADER_DISCLAIMERS[item.type].find(key => !!disclaimers[key]);
    if (!disclaimerKey) {
      return null;
    }

    return <DisclaimerTooltip showLabel disclaimer={disclaimers[disclaimerKey]} data-private data-dd-privacy="mask" tooltipPlace="right" />;
  };

  showAddToGroup = () => {
    const { comparison, item, readOnly, markupMode, editGroups } = this.props;
    const hasGroups = comparison.items.some(item => item.type === COMPARISON_ITEM_TYPES.GROUP);
    const isValidItem = VALID_GROUP_PRODUCTS.includes(item.type as COMPARISON_ITEM_TYPES);

    return editGroups && hasGroups && !readOnly && !markupMode && isValidItem;
  };

  showExpand = () => {
    const { item, readOnly, allowExpandPortfolio, isFXSubItem } = this.props;
    return !readOnly && allowExpandPortfolio && !item._id.startsWith('new-') && !isFXSubItem;
  };

  showFX = () => {
    const { comparison, item, onToggleFXItem, showMenu, markupMode, isEdited } = this.props;

    return onToggleFXItem && showMenu && comparison.showBaseCurrencies && item.currency !== CURRENCY.CAD && !isBenchmark(item) && !markupMode && !isEdited;
  };

  showMenu = (item: ComparisonItem, readOnly?: boolean) => {
    const { showMenu } = this.props;

    if (!showMenu || item._id.startsWith('new-')) {
      return false;
    }

    if (isBenchmark(item)) {
      return !readOnly;
    }

    return true;
  };

  menuIsDisabled = () => {
    const { editedPortfolio, item } = this.props;

    return editedPortfolio && editedPortfolio._id === item._id;
  };

  getComparisonCardMenu = () => {
    const { item, readOnly, user, sharingInfo, markupMode, disableSaving, hasStickyHeader, scrollbarContainerRef, editGroups } = this.props;

    if (!this.showMenu(item, readOnly)) {
      return null;
    }

    const menuIsDisabled = this.menuIsDisabled();

    const menu = (
      <ComparisonCardMenu
        user={user}
        item={item}
        onItemRemove={() => this.props.onItemRemove(item)}
        onModifyPortfolio={() => this.props.onModifyPortfolio(item)}
        onViewPortfolio={() => this.props.onViewPortfolio(item)}
        onExpandPortfolio={(expand: boolean) => this.props.onExpandPortfolioItem(item, expand)}
        onClonePortfolio={() => this.props.onClonePortfolio(item)}
        onAddCategoryBenchmark={() => this.props.addCategoryBenchmark(item)}
        onCloneBenchmark={() => this.props.onCloneBenchmark(item)}
        onSavePortfolio={toContact => this.props.onSavePortfolio(item, toContact)}
        onModifyBenchmark={() => this.props.onModifyBenchmark(item)}
        canEdit={!sharingInfo || sharingInfo.isMine}
        readOnly={readOnly}
        markupMode={markupMode}
        disableSaving={disableSaving}
        disabled={menuIsDisabled}
        scrollbarContainerRef={scrollbarContainerRef}
        hasStickyMenu={hasStickyHeader}
        canEditGroup={editGroups}
        onModifyGroup={() => this.props.onModifyGroup(item)}
      />
    );

    if (menuIsDisabled) {
      return (
        <Tooltip
          icon="icon-pencil"
          title={formatMessage('Comparison.ItemMenu.LockedEditing.Title')}
          message={formatMessage('Comparison.ItemMenu.LockedEditing.Body')}
        >
          <div>{menu}</div>
        </Tooltip>
      );
    }

    return menu;
  };

  getCategoryDisclaimer = (item: ComparisonItem, category: ComparisonCategory) => {
    const { disclaimers } = item;

    if (!disclaimers || !category.disclaimers || !category.disclaimers[item.type]) {
      return null;
    }

    const categoryDisclaimers = category.disclaimers[item.type];
    if (!categoryDisclaimers || !categoryDisclaimers.length) {
      return null;
    }

    const disclaimerKey = categoryDisclaimers.find(d => !!disclaimers[d]);
    if (!disclaimerKey) {
      return null;
    }

    return <DisclaimerTooltip className="category-disclaimer" disclaimer={disclaimers[disclaimerKey]} data-private data-dd-privacy="mask" />;
  };

  getUserInfos = (user: User) => {
    const { comparison } = this.props;

    const comparisonWasShared = comparison.sharedBy && comparison.sharedBy !== user.id;

    let userInfos;

    if (comparisonWasShared) {
      userInfos = {
        imgUrl: comparison.sharedByInfos && comparison.sharedByInfos.imgUrl,
        name: comparison.sharedByInfos && comparison.sharedByInfos.name,
      };
    } else {
      userInfos = {
        imgUrl: user.imgUrl,
        name: `${user.fname} ${user.lname}`,
      };
    }

    return userInfos;
  };

  renderExpandedComment = (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, userInfos: Partial<BasicUserInfos>) => {
    const { user, updateTableHeight, resizeCommentLinesContainer, viewMarkup } = this.props;

    if (!item.markup) {
      return null;
    }

    const comment = item.markup[category.key] && item.markup[category.key][field.key] && item.markup[category.key][field.key].comment;

    const uniqueFieldId = getUniqueFieldId(item, category, field);

    if (!comment || !user) {
      return null;
    }

    return (
      <CommentExpanded
        id={uniqueFieldId}
        dotNumber={comment.dotNumber}
        comment={comment.value}
        isEditing={false}
        isPosted={comment.isPosted}
        timestamp={comment.timestamp && timeSince(comment.timestamp)}
        actionsDisabled
        item={item}
        category={category}
        field={field}
        updateTableHeight={updateTableHeight}
        resizeCommentLinesContainer={resizeCommentLinesContainer}
        userInfos={userInfos}
        viewMarkup={viewMarkup}
      />
    );
  };

  onExpandPortfolioClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    const { item } = this.props;
    this.props.onExpandPortfolioItem(item, !item.isExpanded);
  };

  onFXClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!this.props.onToggleFXItem) {
      return;
    }

    const { item } = this.props;
    this.props.onToggleFXItem(item, !item.isFX);
  };

  showExpandedComment = () => {
    const { item, user, viewMarkup, markupMode, previewMarkup } = this.props;
    const hasMarkupPermission = userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP);
    const showAsFA = !hasMarkupPermission && viewMarkup;
    const showAsAM = hasMarkupPermission && markupMode && previewMarkup;

    return item.markup && (showAsFA || showAsAM);
  };

  getKeyMetricsHeight = (category: ComparisonCategory) => {
    if (category.key !== COMPARISON_CATEGORIES.KEY_METRICS) {
      return undefined;
    }

    return this.props.getAdditionalCategoryStyles(category);
  };

  getSymbols = () => {
    const { isExpandSubItem, item, readOnly, updateComparisonItemHolding } = this.props;

    // Non expanded comparison items of type FUND only have one holding
    // we can look for the list of possible symbols at index 0 of the symbols array
    if (!isExpandSubItem && item.type === COMPARISON_ITEM_TYPES.FUND && item.holdings[0].symbols && item.holdings[0].symbols?.length > 1 && !readOnly) {
      return (
        <SelectInput onValueChange={s => updateComparisonItemHolding(item, item.holdings[0].id, { symbol: s })} selected={item.holdings[0].symbol}>
          {item.holdings[0].symbols.map(s => (
            <SelectOption key={s} value={s}>
              {s}
            </SelectOption>
          ))}
        </SelectInput>
      );
    }

    if (item.type === COMPARISON_ITEM_TYPES.BENCHMARK && item.holdings.length > 1) {
      return '';
    }

    return (
      <span className="comparison-title-text">
        {item.holdings
          .map(h => h.symbol)
          .filter(symbol => !!symbol)
          .join(', ')}
      </span>
    );
  };

  showCustomDisclaimer = (e: React.MouseEvent) => {
    // Prevent card selection from clicking on the disclaimer
    e.stopPropagation();

    this.setState({ showCustomDisclaimerModal: true });
  };

  hideCustomDisclaimerModal = () => {
    this.setState({ showCustomDisclaimerModal: false });
  };

  getCustomDisclaimerModalContent = () => {
    const { item } = this.props;
    const disclaimers = getCustomDisclaimersForItem(item, [DISCLAIMER_TAG.COMPARISON]);

    return (
      <>
        {disclaimers.map(d => (
          <React.Fragment key={d.symbol}>
            <h3>{d.name}</h3>
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatLocalizedString(d.content)) }} />
          </React.Fragment>
        ))}
      </>
    );
  };

  getRiskProfilePill = () => {
    const { editedPortfolio, comparison, item, isSelected } = this.props;

    const profileToShow =
      comparison.riskProfile ||
      (isSelected && editedPortfolio ? editedPortfolio.riskProfile && editedPortfolio.riskProfile.profile : undefined) ||
      item.riskProfile;

    if (profileToShow === undefined) {
      return null;
    }

    return <ComparisonRiskProfileLabel profile={profileToShow} isComparisonRiskProfile={comparison.riskProfile !== undefined} dark={isSelected} />;
  };

  onRemoveItemFromGroup = (group: ComparisonItemGroup) => {
    if (this.props.onRemoveItemFromGroup) {
      this.props.onRemoveItemFromGroup(this.props.item, group);
    }
  };

  onAddItemToGroup = (group: ComparisonItemGroup, value: number, fee?: number) => {
    if (this.props.onAddItemToGroup) {
      this.props.onAddItemToGroup(this.props.item, group, value, fee);
    }
  };

  renderExpandIcon = () => {
    const { hasStickyHeader, item, user } = this.props;
    const isPortfolioExpandLimited = shouldExpandPortfolioInReadOnly(item, user);
    const tooltipMessage = formatMessage(isPortfolioExpandLimited ? 'Comparison.PortfolioExpandLimited' : `Common.${item.isExpanded ? 'Collapse' : 'Expand'}`);

    return (
      <IconButton
        className={classNames('expand-portfolio-btn', { active: item.isExpanded })}
        icon="expand"
        onClick={this.onExpandPortfolioClick}
        tooltip={tooltipMessage}
        tooltipClassName={classNames({ 'has-long-info': isPortfolioExpandLimited })}
        disabled={isPortfolioExpandLimited}
        tooltipPlace={!hasStickyHeader ? 'top' : 'bottom'}
      />
    );
  };

  render() {
    const {
      item,
      readOnly,
      categories,
      sharingInfo,
      user,
      markupMode,
      viewMarkup,
      markupTool,
      setMarkupComment,
      postMarkupComment,
      getNewDotNumber,
      clearMarkup,
      riskPeriod,
      onShowCorrelation,
      previewMarkup,
      comparison,
      isFXSubItem,
      disableStickyHeader,
      isExpandSubItem,
      editedBenchmark,
      popoverContainerRef,
      scrollbarContainerRef,
      hasStickyHeader,
      showGroups,
      editGroups,
      locale,
    } = this.props;

    const { showCustomDisclaimerModal } = this.state;

    if (!user) {
      return null;
    }

    const commentSection = document.getElementById(`comment-section-${item._id}`);
    const userInfos = this.getUserInfos(user);
    const showCurrency = !isFXSubItem && comparison.showBaseCurrencies && userHasPermissionEnabled(user, PERMISSIONS.CURRENCY_CONVERSIONS);
    const showPortfolioType =
      !isFXSubItem && !item.providedBy && item.portfolioType && !item._id.startsWith('new-') && item.type === COMPARISON_ITEM_TYPES.PORTFOLIO;
    const showLongerHistory = comparison.shortHistory && item.disclaimers && item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS];
    const showCustomItemDisclaimer = getCustomDisclaimersForItem(item, [DISCLAIMER_TAG.COMPARISON]).length > 0;
    const showAddToGroup = this.showAddToGroup();

    const itemName = formatLocalizedString(item.name);
    const stickyOffset = isFXSubItem || isExpandSubItem ? -28 : -20;
    const comparisonGroups = comparison.items.filter(item => item.type === COMPARISON_ITEM_TYPES.GROUP);

    return (
      <>
        <StickyContainer className="legacy-comparison-card-content" data-testid="legacy-comparison-card-content">
          <Sticky disableCompensation={disableStickyHeader} topOffset={stickyOffset}>
            {({ style, isSticky }) => {
              const showEnterpriseInfo = !item.providedBy && !!item.enterprise && !isSticky;
              const showCardHeaderDisclaimer = !showLongerHistory && !isSticky;
              const sticky = isSticky && !disableStickyHeader;

              return (
                <div className={classNames('comparison-card-header', { 'is-sticky': sticky })} style={sticky ? style : undefined}>
                  <div className="header-top-row">
                    <div className="top-row-info">
                      {sharingInfo && (
                        <div className="comparison-card-sharing-info">
                          <i className="icon-user" />
                          <span>{sharingInfo.label}</span>
                        </div>
                      )}

                      {!isFXSubItem && item.holdings.length > 0 && (
                        <div className="comparison-card-title d-flex align-items-center">
                          <ComparisonCardHoldingIcon
                            item={item}
                            updateComparisonItemColor={this.props.updateComparisonItemColor}
                            popoverContainerRef={popoverContainerRef}
                            scrollbarContainerRef={scrollbarContainerRef}
                            disableColorPicker={isExpandSubItem || isSticky}
                          />
                          {this.getSymbols()}
                        </div>
                      )}

                      {isFXSubItem && (
                        <div className="comparison-card-title">
                          <HoldingIcon type={item.type} color={item.color} />

                          <span className="comparison-title-text">
                            <FormattedMessage id="Comparison.ConvertedToCAD" />
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="comparison-card-actions">
                      {showAddToGroup && (
                        <ComparisonGroupsMenu
                          item={item}
                          allGroupsItems={comparisonGroups.map(group => ({
                            name: group.name,
                            itemId: group._id,
                            color: group.color,
                          }))}
                          onRemoveFromGroup={this.onRemoveItemFromGroup}
                          onAddToGroup={this.onAddItemToGroup}
                          scrollbarContainerRef={scrollbarContainerRef}
                          positionFixed={!hasStickyHeader}
                          menuTooltip={formatMessage('Comparison.Groups.Tooltips.MenuAdd')}
                          menuIcon="new-group"
                          editGroups={!readOnly}
                        />
                      )}

                      {this.showFX() && (
                        <IconButton
                          className={classNames('fx-btn', { active: item.isFX })}
                          icon="fx"
                          onClick={this.onFXClick}
                          tooltip={formatMessage('Comparison.CompareCAD')}
                        />
                      )}

                      {this.showExpand() && this.renderExpandIcon()}

                      {this.getComparisonCardMenu()}
                    </div>
                  </div>

                  <div data-private data-dd-privacy="mask" className="comparison-card-name">
                    <Tooltip message={itemName} delayShow={300} data-private data-dd-privacy="mask">
                      <span>
                        <Dotdotdot useNativeClamp clamp={2}>
                          {itemName}
                        </Dotdotdot>
                      </span>
                    </Tooltip>

                    {item.flags && <ShelfStatus statuses={item.flags} />}

                    <div className="comparison-card-labels">
                      {showPortfolioType && item.portfolioType && <PortfolioTypeLabel size="extraSmall" portfolioType={item.portfolioType} />}

                      {showCurrency && item.currency && <CurrencyLabel currency={item.currency} />}

                      {item.flags && item.flags.length > 0 && <CustomTypePills flaggableInstrument={item} />}
                    </div>
                  </div>

                  {!!item.contactsInfo && <ComparisonCardContacts contacts={item.contactsInfo} />}

                  {item.flags && <ComparisonCardWarnings warnings={item.flags} />}

                  {showEnterpriseInfo && item.enterprise && (
                    <div className="comparison-card-enterprise">
                      <span className="comparison-card-icon">
                        <Tooltip
                          title={formatMessage('Models.EnterpriseLockTooltip.Title', { name: item.enterprise.name })}
                          message={formatMessage('Models.EnterpriseLockTooltip.Message')}
                        >
                          <i className="icon-lock-closed" style={{ color: item.color }} />
                        </Tooltip>
                      </span>
                      <span className="comparison-title-text">
                        <FormattedMessage id="Models.EnterpriseLockLabel" values={{ name: item.enterprise.name }} />
                      </span>
                    </div>
                  )}

                  {!item.enterprise && !sharingInfo && item.overrideHoldings?.length && (
                    <div className="comparison-card-limited-edits">
                      <span className="comparison-card-icon">
                        <Tooltip title={formatMessage('Models.LimitedEditingTooltip.Title')} message={formatMessage('Models.LimitedEditingTooltip.Message')}>
                          <i className="icon-lock-outline" style={{ color: item.color }} />
                        </Tooltip>
                      </span>
                      <span className="comparison-title-text">
                        <FormattedMessage id="Models.LimitedEditingTooltip.Title" />
                      </span>
                    </div>
                  )}

                  {showCardHeaderDisclaimer && this.getCardHeaderDisclaimer()}

                  {showLongerHistory && (
                    <div className="comparison-card-disclaimer">
                      <Tooltip
                        title={formatMessage('Comparison.LongHistoryTooltip.Title')}
                        message={formatMessage('Comparison.LongHistoryTooltip.Message')}
                        list={getShortHistoryDatesForItem(item)}
                        place="right"
                      >
                        <span>
                          <i className="icon-info" />
                          <FormattedMessage id="Comparison.ShowingLongerHistory" />
                        </span>
                      </Tooltip>
                    </div>
                  )}

                  {showCustomItemDisclaimer && (
                    <div className="comparison-card-disclaimer custom-item-disclaimer">
                      <PrimaryLink onClick={this.showCustomDisclaimer}>
                        <i className="icon-info" />
                        <FormattedMessage id="Comparison.CustomFundDisclaimer.Title" />
                      </PrimaryLink>
                    </div>
                  )}

                  {this.getRiskProfilePill()}

                  {showGroups && (
                    <ComparisonItemGroups
                      item={item}
                      onRemoveGroup={this.onRemoveItemFromGroup}
                      grouped
                      scrollbarContainerRef={scrollbarContainerRef}
                      positionFixed={!hasStickyHeader}
                      editGroups={editGroups}
                    />
                  )}
                </div>
              );
            }}
          </Sticky>

          <div className="comparison-categories">
            {categories.map(category => {
              const showEditBenchmark = isBenchmark(item) && category.key === COMPARISON_CATEGORIES.KEY_METRICS;
              const categoryIncludesCurrentItem = !category.itemTypes || category.itemTypes.includes(item.type);
              const showCategoryFields = comparisonCategoryIsShown(comparison, category);

              const categoryDynamicDisclaimer = category.getDisclaimer && category.getDisclaimer(item);
              const categoryDisclaimer = this.getCategoryDisclaimer(item, category);
              const showDisclaimer = categoryIncludesCurrentItem && showCategoryFields && (categoryDisclaimer != null || categoryDynamicDisclaimer != null);

              return (
                <div className={`comparison-category ${category.key}`} style={this.getKeyMetricsHeight(category)} key={category.key}>
                  {!showEditBenchmark && (
                    <ComparisonBaseCell className={classNames('category-header', { controlled: category.controlled, 'has-content': showDisclaimer })}>
                      {showDisclaimer && (
                        <div className="section-disclaimer">
                          <span>
                            {categoryDynamicDisclaimer}
                            {categoryDisclaimer}
                          </span>
                        </div>
                      )}
                    </ComparisonBaseCell>
                  )}

                  {showEditBenchmark && (
                    <ComparisonBenchmark
                      editedBenchmark={editedBenchmark}
                      user={user}
                      readOnly={readOnly}
                      item={item}
                      showCurrencies={!!comparison.showBaseCurrencies}
                    />
                  )}

                  {!showEditBenchmark &&
                    showCategoryFields &&
                    category.fields.map(field => (
                      <React.Fragment key={field.key}>
                        <ComparisonValueCell
                          user={user}
                          riskPeriod={riskPeriod}
                          comparisonItem={item}
                          category={category}
                          field={field}
                          viewMarkup={viewMarkup}
                          markupTool={markupTool}
                          markupMode={markupMode}
                          onClick={markupMode ? () => this.props.onCellClick(item, category, field) : undefined}
                          setMarkupComment={setMarkupComment}
                          postMarkupComment={postMarkupComment}
                          clearMarkup={clearMarkup}
                          getNewDotNumber={getNewDotNumber}
                          userInfos={userInfos}
                          onShowCorrelation={onShowCorrelation}
                          previewMarkup={previewMarkup}
                          showBaseCurrencyValues={!!comparison.showBaseCurrencies && !isFXSubItem}
                          useLongHistory={comparison.shortHistory}
                          comparison={comparison}
                          readOnly={readOnly}
                          locale={locale}
                        />

                        {field.subFields &&
                          comparison.expandedFields?.includes(field.key) &&
                          field.subFields.map(subField => (
                            <React.Fragment key={`${field.key}-${subField.key}`}>
                              <ComparisonValueCell
                                user={user}
                                riskPeriod={riskPeriod}
                                comparisonItem={item}
                                category={category}
                                viewMarkup={viewMarkup}
                                markupTool={markupTool}
                                markupMode={markupMode}
                                onClick={markupMode ? () => this.props.onCellClick(item, category, subField) : undefined}
                                setMarkupComment={setMarkupComment}
                                postMarkupComment={postMarkupComment}
                                clearMarkup={clearMarkup}
                                getNewDotNumber={getNewDotNumber}
                                userInfos={userInfos}
                                onShowCorrelation={onShowCorrelation}
                                previewMarkup={previewMarkup}
                                showBaseCurrencyValues={!!comparison.showBaseCurrencies && !isFXSubItem}
                                useLongHistory={comparison.shortHistory}
                                comparison={comparison}
                                readOnly={readOnly}
                                locale={locale}
                                field={subField}
                              />
                            </React.Fragment>
                          ))}

                        {this.showExpandedComment() &&
                          commentSection &&
                          ReactDOM.createPortal(this.renderExpandedComment(item, category, field, userInfos), commentSection)}
                      </React.Fragment>
                    ))}
                </div>
              );
            })}
          </div>
        </StickyContainer>

        <GenericTextModal
          title={formatMessage('Common.Disclaimer')}
          show={showCustomDisclaimerModal}
          onCancel={this.hideCustomDisclaimerModal}
          body={this.getCustomDisclaimerModalContent()}
        />
      </>
    );
  }
}

export default LegacyComparisonCardContent;
